import * as THREE from 'three'
import Experience from './Experience.js'
import vertexShader from './Shaders/textVertex.glsl'
import fragmentShader from './Shaders/textFragment.glsl'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'

export default class Text {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.sizes = this.experience.sizes
        this.camera = this.experience.camera.instance
        this.debug = this.experience.debug

        this.mouseUV = new THREE.Vector2(0.5, 0.5)
        this.prevMouseUV = new THREE.Vector2(0.5, 0.5)
        this.targetMouseUV = new THREE.Vector2(0.5, 0.5)
        this.trailUV = new THREE.Vector2(0.5, 0.5)

        this.inputNDC = new THREE.Vector2(0, 0)
        this.raycaster = new THREE.Raycaster()
        window.addEventListener('pointermove', this.onPointerMove.bind(this))
        this.mouseRadius = 0.5

        this.fontLoader = new FontLoader()
        this.font = null

        // Create separate trail textures for each text mesh
        this.trailTextures = {
            left: null,
            right: null,
            center: null
        };
        
        // Setup individual canvases for each text mesh
        this.setupTrailCanvases();

        // Load default font if not already loaded
        this.loadDefaultFont()

        // Add resize listener
        this.sizes.on('resize', () => {
            this.updateSize()
        })
    }

    loadDefaultFont() {
        this.fontLoader.load(
            'https://threejs.org/examples/fonts/helvetiker_regular.typeface.json',
            (font) => {
                this.font = font
                this.setGeometries()
                this.setMaterial()
                this.setMeshes()
                this.createProxyMeshes()

                if (this.debug.active) {
                    this.debugFolder = this.debug.ui.addFolder('Text')
                    this.debugFolder.add(this.leftMesh.position, 'x', -5, 5, 0.01).name('Left X')
                    this.debugFolder.add(this.leftMesh.position, 'y', -5, 5, 0.01).name('Left Y')
                    this.debugFolder.add(this.rightMesh.position, 'x', -5, 5, 0.01).name('Right X')
                    this.debugFolder.add(this.rightMesh.position, 'y', -5, 5, 0.01).name('Right Y')
                    this.debugFolder.add(this.centerMesh.position, 'x', -5, 5, 0.01).name('Center X')
                    this.debugFolder.add(this.centerMesh.position, 'y', -5, 5, 0.01).name('Center Y')
                    this.debugFolder.add({ effectRadius: 0.3 }, 'effectRadius', 0.05, 1.0, 0.05)
                        .name('Effect Radius')
                        .onChange((value) => {
                            ['left', 'right', 'center'].forEach(position => {
                                if (this.materials[position]) {
                                    this.materials[position].uniforms.uEffectRadius.value = value;
                                }
                            });
                        });
                }
            },
            undefined,
            (error) => {
                console.error('Default font loading failed:', error)
            }
        )
    }

    setGeometries() {
        if (!this.font) return

        this.leftGeometry = new TextGeometry('We work with brands at\ncrossroads to redefine\nwhat comes next.', {
            font: this.font,
            size: 0.8, 
            depth: 0,
            curveSegments: 12,
            bevelEnabled: false
        })
        this.leftGeometry.computeBoundingBox()
        this.leftGeometry.center()

        this.rightGeometry = new TextGeometry('Croswalk Studio/\nMove differently.', {
            font: this.font,
            size: 0.1,
            depth: 0,
            curveSegments: 12,
            bevelEnabled: false
        })
        this.rightGeometry.computeBoundingBox()
        this.rightGeometry.center()

        this.centerGeometry = new TextGeometry('Website Under Construction', {
            font: this.font,
            size: 0.08,
            depth: 0,
            curveSegments: 12,
            bevelEnabled: false
        })
        this.centerGeometry.computeBoundingBox()
        this.centerGeometry.center()
    }

    setMaterial() {
        // Create separate materials for each text mesh
        this.materials = {};
        
        ['left', 'right', 'center'].forEach(position => {
            this.materials[position] = new THREE.ShaderMaterial({
                vertexShader: vertexShader,
                fragmentShader: fragmentShader,
                uniforms: {
                    uColor: { value: new THREE.Color(0xffffff) },
                    uTime: { value: 0 },
                    uMouseWorldPosition: { value: new THREE.Vector3(-1000, -1000, 0) }, // Off-screen
                    uEffectRadius: { value: 0.3 }, // Radius in world units
                    uGridSize: { value: 64.0 }
                },
                transparent: true
            });
        });
    }

    setupTrailCanvases() {
        // Create canvases for each text mesh
        this.canvases = {};
        this.contexts = {};
        
        // For each text position (left, right, center)
        ['left', 'right', 'center'].forEach(position => {
            // Create canvas
            const canvas = document.createElement('canvas');
            canvas.width = 256;
            canvas.height = 256;
            const ctx = canvas.getContext('2d');
            
            // Fill with black initially
            ctx.fillStyle = 'black';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Create texture
            const texture = new THREE.CanvasTexture(canvas);
            texture.needsUpdate = true;
            
            // Store references
            this.canvases[position] = canvas;
            this.contexts[position] = ctx;
            this.trailTextures[position] = texture;
        });
        
        // Trail parameters
        this.mouseRadius = 1.5;
        this.trailFalloff = 0.9;
        this.distortionStrength = 10.0;
        this.fadeSpeed = 0.05;
        
        // Mouse state tracking for each position
        this.mouseState = {
            left: { lastX: 999, lastY: 999, isMoving: false, timeout: null },
            right: { lastX: 999, lastY: 999, isMoving: false, timeout: null },
            center: { lastX: 999, lastY: 999, isMoving: false, timeout: null }
        };
        
        // Setup raycaster for UV coordinates
        this.raycaster = new THREE.Raycaster();
    }

    setMeshes() {
        // Create meshes with their individual materials
        if (this.leftGeometry) {
            this.leftMesh = new THREE.Mesh(this.leftGeometry, this.materials.left);
            this.scene.add(this.leftMesh);
        }
        
        if (this.rightGeometry) {
            this.rightMesh = new THREE.Mesh(this.rightGeometry, this.materials.right);
            this.scene.add(this.rightMesh);
        }
        
        if (this.centerGeometry) {
            this.centerMesh = new THREE.Mesh(this.centerGeometry, this.materials.center);
            this.scene.add(this.centerMesh);
        }
        
        // Store mesh to position mapping for easier lookup
        this.meshPositionMap = new Map();
        if (this.leftMesh) this.meshPositionMap.set(this.leftMesh, 'left');
        if (this.rightMesh) this.meshPositionMap.set(this.rightMesh, 'right');
        if (this.centerMesh) this.meshPositionMap.set(this.centerMesh, 'center');
        
        this.updateSize();
    }

    createProxyMeshes() {
        // Create invisible proxy planes for each text mesh
        this.proxyMeshes = {};
        this.proxyMaterials = {};
        
        ['left', 'right', 'center'].forEach(position => {
            if (!this[`${position}Mesh`]) return;
            
            // Get the bounding box of the text geometry
            const textMesh = this[`${position}Mesh`];
            const geometry = textMesh.geometry;
            geometry.computeBoundingBox();
            
            // Calculate dimensions
            const width = geometry.boundingBox.max.x - geometry.boundingBox.min.x;
            const height = geometry.boundingBox.max.y - geometry.boundingBox.min.y;
            
            // Create a plane geometry with LARGER dimensions (padding around the text)
            // Add padding based on text size - more padding for smaller text
            let paddingFactor;
            if (position === 'left') {
                paddingFactor = 0.1; // 10% padding for large text
            } else {
                paddingFactor = 0.5; // 50% padding for small text
            }
            
            const paddedWidth = width * (1 + paddingFactor);
            const paddedHeight = height * (1 + paddingFactor);
            
            // Higher resolution for smoother distortion
            const segmentsX = 48; // Even higher resolution
            const segmentsY = 24; // Even higher resolution
            const planeGeometry = new THREE.PlaneGeometry(paddedWidth, paddedHeight, segmentsX, segmentsY);
            
            // Create a simple material for the proxy (invisible)
            const material = new THREE.MeshBasicMaterial({ 
                transparent: true, 
                opacity: 0.0, // Invisible
                side: THREE.DoubleSide
            });
            
            // Create the proxy mesh
            const proxyMesh = new THREE.Mesh(planeGeometry, material);
            
            // Position the proxy to match the text
            proxyMesh.position.copy(textMesh.position);
            proxyMesh.rotation.copy(textMesh.rotation);
            proxyMesh.scale.copy(textMesh.scale);
            
            // Add to scene
            this.scene.add(proxyMesh);
            
            // Store reference
            this.proxyMeshes[position] = proxyMesh;
            
            // Create a mapping between proxy mesh and position
            this.proxyMeshPositionMap = this.proxyMeshPositionMap || new Map();
            this.proxyMeshPositionMap.set(proxyMesh, position);
            
            // For debugging - make the proxy visible temporarily
            // Uncomment to see the proxy meshes
            /*
            const debugMaterial = new THREE.MeshBasicMaterial({ 
                color: 0xff0000,
                wireframe: true,
                transparent: true,
                opacity: 0.3
            });
            const debugMesh = new THREE.Mesh(planeGeometry.clone(), debugMaterial);
            debugMesh.position.copy(proxyMesh.position);
            debugMesh.rotation.copy(proxyMesh.rotation);
            debugMesh.scale.copy(proxyMesh.scale);
            this.scene.add(debugMesh);
            */
        });
    }

    onPointerMove(event) {
        // Get normalized device coordinates
        const ndcX = (event.clientX / this.sizes.width) * 2 - 1;
        const ndcY = -(event.clientY / this.sizes.height) * 2 + 1;
        
        // Create a vector for the mouse position in NDC
        const mouseNDC = new THREE.Vector2(ndcX, ndcY);
        
        // Create a raycaster
        this.raycaster.setFromCamera(mouseNDC, this.camera);
        
        // Reset all mouse positions to off-screen
        ['left', 'right', 'center'].forEach(position => {
            if (this.materials[position]) {
                this.materials[position].uniforms.uMouseWorldPosition.value.set(-1000, -1000, 0);
            }
        });
        
        // Check intersections with proxy meshes
        const proxyMeshes = Object.values(this.proxyMeshes).filter(mesh => mesh);
        const proxyIntersects = this.raycaster.intersectObjects(proxyMeshes);
        
        if (proxyIntersects.length > 0) {
            // Get the intersection point in world space
            const intersectionPoint = proxyIntersects[0].point;
            const intersectedMesh = proxyIntersects[0].object;
            
            // Get the position (left, right, center) of the intersected proxy mesh
            const position = this.proxyMeshPositionMap.get(intersectedMesh);
            
            if (position && this.materials[position]) {
                // Update the mouse world position uniform
                this.materials[position].uniforms.uMouseWorldPosition.value.copy(intersectionPoint);
                
                // Log for debugging
                console.log(`Mouse over ${position} at world pos: ${intersectionPoint.x.toFixed(2)}, ${intersectionPoint.y.toFixed(2)}`);
            }
        }
    }

    drawGlowAtPosition(position, x, y) {
        const ctx = this.contexts[position];
        const canvas = this.canvases[position];
        
        if (!ctx || !canvas) return;
        
        // Apply a fade effect based on the trail falloff
        const fadeAmount = 1 - this.trailFalloff;
        ctx.fillStyle = `rgba(0, 0, 0, ${fadeAmount})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Calculate the size of the glow in pixels
        const pixelRadius = this.mouseRadius * Math.min(canvas.width, canvas.height);
        
        // Create a radial gradient for the glow
        const gradient = ctx.createRadialGradient(
            x, y, 0,
            x, y, pixelRadius
        );
        
        // Add color stops for a nice glow effect (blue glow for text)
        gradient.addColorStop(0, 'rgba(0, 150, 255, 1)'); // Bright blue at center
        gradient.addColorStop(0.3, 'rgba(0, 100, 220, 0.8)'); // Medium blue
        gradient.addColorStop(0.7, 'rgba(0, 50, 180, 0.4)'); // Dark blue
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)'); // Transparent at edge
        
        // Draw the glow
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, pixelRadius, 0, Math.PI * 2);
        ctx.fill();
        
        // Update the texture
        this.trailTextures[position].needsUpdate = true;
    }

    update() {
        const time = this.experience.time.elapsed * 0.001;
        
        // Update time uniform for all materials
        if (this.materials) {
            ['left', 'right', 'center'].forEach(position => {
                if (this.materials[position]) {
                    this.materials[position].uniforms.uTime.value = time;
                }
            });
        }
    }

    updateSize() {
        if (!this.leftMesh || !this.rightMesh || !this.centerMesh || !this.camera) return

        const camera = this.camera
        const distance = Math.abs(camera.position.z)
        const vFov = camera.fov * Math.PI / 180
        const viewportHeight = 2 * Math.tan(vFov / 2) * distance
        const viewportWidth = viewportHeight * camera.aspect

        // Get bounding boxes for each text mesh
        this.leftGeometry.computeBoundingBox()
        this.rightGeometry.computeBoundingBox()
        this.centerGeometry.computeBoundingBox()

        // Calculate text widths
        const leftWidth = this.leftGeometry.boundingBox.max.x - this.leftGeometry.boundingBox.min.x
        const rightWidth = this.rightGeometry.boundingBox.max.x - this.rightGeometry.boundingBox.min.x
        const centerWidth = this.centerGeometry.boundingBox.max.x - this.centerGeometry.boundingBox.min.x

        // Calculate text heights
        const leftHeight = this.leftGeometry.boundingBox.max.y - this.leftGeometry.boundingBox.min.y
        const rightHeight = this.rightGeometry.boundingBox.max.y - this.rightGeometry.boundingBox.min.y
        const centerHeight = this.centerGeometry.boundingBox.max.y - this.centerGeometry.boundingBox.min.y

        // Check if we're in mobile view
        const isMobile = this.sizes.width <= 480

        // Set different values based on viewport size
        let maxWidthPercentage, topPadding, leftPadding, rightPadding, centerTopPadding

        if (isMobile) {
            // Mobile values
            maxWidthPercentage = 0.8  // Larger percentage of viewport width for mobile
            topPadding = viewportHeight * 0.15  // 15% from top
            leftPadding = viewportWidth * 0.05   // 5% from left
            rightPadding = viewportWidth * 0.05  // 5% from right
            centerTopPadding = viewportHeight * 0.5  // 50% from top
        } else {
            // Desktop values
            maxWidthPercentage = 0.3
            topPadding = viewportHeight * 0.03
            leftPadding = viewportWidth * 0.01
            rightPadding = viewportWidth * 0.40
            centerTopPadding = viewportHeight * 0.32
        }

        // Calculate scales
        const desiredWidth = viewportWidth * maxWidthPercentage
        const baseScale = isMobile ? 0.6 : 0.4  // Different base scale for mobile

        // Calculate individual scales
        const leftScale = Math.min(desiredWidth / leftWidth, baseScale)
        const rightScale = Math.min(desiredWidth / rightWidth, baseScale)
        const centerScale = Math.min(desiredWidth / centerWidth, baseScale)

        // Apply scales
        this.leftMesh.scale.set(leftScale, leftScale, leftScale)
        this.rightMesh.scale.set(rightScale, rightScale, rightScale)
        this.centerMesh.scale.set(centerScale, centerScale, centerScale)

        // Calculate actual scaled widths
        const scaledLeftWidth = leftWidth * leftScale
        const scaledRightWidth = rightWidth * rightScale
        const scaledCenterWidth = centerWidth * centerScale

        if (isMobile) {
            // Mobile positioning
            this.leftMesh.position.set(
                -viewportWidth/2 + leftPadding + (scaledLeftWidth/2),
                1.7,
                0
            )

            this.rightMesh.position.set(
                -viewportWidth/2 + leftPadding + (scaledRightWidth/2),
                1.1,
                0
            )

            this.centerMesh.position.set(
                -viewportWidth/2 + leftPadding + (scaledCenterWidth/2), 
                0,  
                0
            )
        } else {
            // Desktop positioning - align from top edge
            const scaledLeftHeight = leftHeight * leftScale
            const scaledRightHeight = rightHeight * rightScale
            
            // Calculate Y positions accounting for mesh heights
            const topEdge = viewportHeight/2 - topPadding
            const leftY = topEdge - (scaledLeftHeight/2)
            const rightY = topEdge - (scaledRightHeight/2)

            // Left mesh
            this.leftMesh.position.set(
                -viewportWidth/2 + leftPadding + (scaledLeftWidth/2),
                leftY,
                0
            )

            // Right mesh
            this.rightMesh.position.set(
                viewportWidth/2 - rightPadding,
                rightY,
                0
            )

            // Center mesh
            this.centerMesh.position.set(
                viewportWidth/2 - rightPadding,
                viewportHeight/2 - centerTopPadding,
                0
            )
        }

        // Update material aspect ratio
        if (this.material) {
            this.material.uniforms.uAspectRatio.value = viewportWidth / viewportHeight
        }

        // Update proxy meshes
        if (this.proxyMeshes) {
            this.updateProxyMeshes();
        }
    }

    updateProxyMeshes() {
        // Update proxy meshes to match text meshes
        ['left', 'right', 'center'].forEach(position => {
            const textMesh = this[`${position}Mesh`];
            const proxyMesh = this.proxyMeshes[position];
            
            if (textMesh && proxyMesh) {
                // Update position, rotation, and scale
                proxyMesh.position.copy(textMesh.position);
                proxyMesh.rotation.copy(textMesh.rotation);
                proxyMesh.scale.copy(textMesh.scale);
            }
        });
    }

    debug() {
        // Add debug canvases to the DOM to see what's being drawn
        ['left', 'right', 'center'].forEach(position => {
            const canvas = this.canvases[position];
            if (canvas) {
                canvas.style.position = 'fixed';
                canvas.style.top = position === 'left' ? '10px' : position === 'right' ? '10px' : '10px';
                canvas.style.left = position === 'left' ? '10px' : position === 'right' ? '276px' : '542px';
                canvas.style.width = '256px';
                canvas.style.height = '256px';
                canvas.style.zIndex = '1000';
                canvas.style.border = '1px solid white';
                document.body.appendChild(canvas);
            }
        });
        
        // Log when intersections happen
        const originalOnPointerMove = this.onPointerMove.bind(this);
        this.onPointerMove = (event) => {
            // Call the original method
            originalOnPointerMove(event);
            
            // Log intersection information
            const ndcX = (event.clientX / this.sizes.width) * 2 - 1;
            const ndcY = -(event.clientY / this.sizes.height) * 2 + 1;
            this.raycaster.setFromCamera({ x: ndcX, y: ndcY }, this.camera);
            const meshes = [this.leftMesh, this.rightMesh, this.centerMesh].filter(mesh => mesh);
            const intersects = this.raycaster.intersectObjects(meshes);
            
            if (intersects.length > 0) {
                console.log('Intersection detected with:', this.meshPositionMap.get(intersects[0].object));
            }
        };
    }
}